import React, { useState, useRef, useCallback } from 'react';
import { adminApi } from '../lib/api';
import { toast } from 'sonner';
import { Upload, ImageIcon, Trash2, RefreshCw, Loader2, FileImage } from 'lucide-react';

const ALLOWED = ['image/png', 'image/svg+xml', 'image/webp', 'image/jpeg', 'image/jpg'];
const ALLOWED_EXT = ['png', 'svg', 'webp', 'jpg', 'jpeg'];

export const MediaUpload = ({ value, filename, onChange, testIdPrefix = 'media', optimize = false }) => {
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const uploadFile = useCallback(async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED.includes(file.type) && !ALLOWED_EXT.includes(ext)) {
      toast.error('Format non autorisé. Utilisez PNG, SVG, WebP ou JPG.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Le fichier dépasse 10 Mo.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (optimize) formData.append('optimize', 'true');
      const res = await adminApi.uploadMedia(formData);
      const rawUrl = res.data.url;
      const fullUrl = rawUrl.startsWith('http')
        ? rawUrl
        : `${process.env.REACT_APP_BACKEND_URL}${rawUrl}`;
      onChange(fullUrl, res.data.filename);
      toast.success('Image téléversée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléversement');
    } finally {
      setUploading(false);
    }
  }, [onChange, optimize]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    uploadFile(file);
  };

  const handleSelect = (e) => {
    const file = e.target.files?.[0];
    uploadFile(file);
    e.target.value = '';
  };

  const openPicker = () => inputRef.current?.click();

  return (
    <div data-testid={`${testIdPrefix}-upload`}>
      <input
        ref={inputRef}
        type="file"
        accept=".png,.svg,.webp,.jpg,.jpeg,image/png,image/svg+xml,image/webp,image/jpeg"
        onChange={handleSelect}
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
      />

      {value ? (
        <div className="flex items-center gap-3">
          <div className="w-20 h-20 flex-shrink-0 bg-[#F9F6F0] rounded-lg border border-[#E3DED5] flex items-center justify-center overflow-hidden">
            <img src={value} alt="Aperçu" className="max-w-full max-h-full object-contain" data-testid={`${testIdPrefix}-preview`} />
          </div>
          <div className="flex-1 min-w-0">
            {filename && (
              <p className="text-sm text-[#2A312C] truncate flex items-center gap-1" data-testid={`${testIdPrefix}-filename`}>
                <FileImage className="w-4 h-4 flex-shrink-0 text-[#A5BBA8]" />
                {filename}
              </p>
            )}
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={openPicker}
                disabled={uploading}
                className="text-sm px-3 py-1.5 rounded-lg bg-[#A5BBA8] text-white hover:bg-[#94aa97] transition-colors flex items-center gap-1 disabled:opacity-50"
                data-testid={`${testIdPrefix}-replace-btn`}
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Remplacer
              </button>
              <button
                type="button"
                onClick={() => onChange('', '')}
                disabled={uploading}
                className="text-sm px-3 py-1.5 rounded-lg bg-[#F9F6F0] text-[#C86B51] hover:bg-[#f0ebe0] transition-colors flex items-center gap-1 disabled:opacity-50"
                data-testid={`${testIdPrefix}-delete-btn`}
              >
                <Trash2 className="w-4 h-4" />
                Supprimer
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div
          onClick={openPicker}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
            dragging ? 'border-[#A5BBA8] bg-[#A5BBA8]/10' : 'border-[#E3DED5] hover:border-[#A5BBA8] bg-[#F9F6F0]'
          }`}
          data-testid={`${testIdPrefix}-dropzone`}
        >
          {uploading ? (
            <div className="text-[#5C6A61]">
              <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-[#A5BBA8]" />
              <p className="text-sm">Téléversement...</p>
            </div>
          ) : (
            <div className="text-[#5C6A61]">
              <Upload className="w-8 h-8 mx-auto mb-2 text-[#A5BBA8]" />
              <button
                type="button"
                className="text-sm font-medium text-[#2A312C] underline"
                data-testid={`${testIdPrefix}-choose-btn`}
              >
                Choisir une image
              </button>
              <p className="text-xs mt-1">ou glissez-déposez ici</p>
              <p className="text-xs mt-1 opacity-70">PNG, SVG, WebP, JPG · max 10 Mo</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MediaUpload;
